import React, { useCallback, useState } from 'react'

import { useFlag } from '@unleash/proxy-client-react'
import { Box, Divider, Flex, FlexItem, Icon, Text } from 'stardust'
import styled from 'styled-components'

import { isHome, isHomeWebview, isPlayground } from '~/config'
import * as AIAssistAnalytics from '~/modules/analytics/aiAssistant'
import colors from '~/ui-components/tokens/colors'

import AiAssistLog from './Tags/AiAssistLog'
import ChildGrid from './Tags/ChildGrid'
import FuturePlans from './Tags/FuturePlans'
import LearningOutcomeGrid from './Tags/LearningOutcomeGrid'
import LinksGrid from './Tags/LinksGrid'
import Reflections from './Tags/Reflections'

const S = {
  Box: styled(Box)<{ backgroundColor: string; hoverColor: string }>`
    background-color: ${(props) => props.backgroundColor};
    border-radius: 40px;
    padding: 12px 8px;
    &:hover {
      background-color: ${(props) => props.hoverColor};
      transition: background-color 0.2s;
    }
  `,
  Divider: styled(Divider)`
    border-bottom-width: 2px;
  `,
}

const HorizontalScroll = styled(Box)`
  overflow-x: auto;
  white-space: nowrap;
`

interface TagProps {
  taggedChildren?: Playground.SimpleChild[]
  learningOutcomes?: Playground.LearningFrameworkOutcome[]
  links?: Playground.Document[]
  futurePlans?: string
  planningDate?: string
  reflections?: Playground.Reflection[]
  aiAssistantLogs?: Playground.AiAssistantResponseLog[]
  postId?: Nullable<number>
  isPostPreview?: boolean
}

interface ButtonProps {
  amount?: number
  tagType: string
}

const Tags = ({
  taggedChildren,
  learningOutcomes,
  links,
  futurePlans,
  planningDate,
  reflections,
  aiAssistantLogs,
  postId,
  isPostPreview,
}: TagProps) => {
  const assistLogFeatureFlag = useFlag('assist_log')
  const [selectedTag, setSelectedTag] = useState<string>('')
  const homeOrPlaygroundColors = isHome ? colors.hullOrange6 : colors.nebulaBlue6
  const isHomeWebviewBackground = isHomeWebview ? colors.cosmicShade10 : colors.cosmicShade4
  const isHomeWebviewText = isHomeWebview ? colors.cosmicShade0 : colors.cosmicShade10

  const TagButtons = ({ amount, tagType }: ButtonProps) => {
    const checkTagType = selectedTag === tagType
    const backgroundColor = checkTagType ? homeOrPlaygroundColors : isHomeWebviewBackground
    const hoverColor = checkTagType ? homeOrPlaygroundColors : colors.cosmicShade18
    const textColor = checkTagType ? colors.cosmicShade0 : isHomeWebviewText
    const dividerColor = checkTagType ? 'primary' : colors.cosmicShade4

    const handleClick = useCallback(() => {
      if (tagType === 'Assist Log') AIAssistAnalytics.openLogs()
      setSelectedTag(tagType)
    }, [tagType])

    return (
      <FlexItem onClick={handleClick} data-qa={`tag-button-${tagType.toLocaleLowerCase().replace(' ', '-')}`}>
        <S.Box
          backgroundColor={backgroundColor}
          hoverColor={hoverColor}
          width="max-content"
          cursor="pointer"
          ml={1}
          mr={1}>
          <Flex>
            <Text fontSize={1} color={textColor} fontWeight="bold" mr={1}>
              {amount}
            </Text>
            <Text
              fontSize={1}
              color={textColor}
              data-qa={`post-detail-tag-${tagType.toLowerCase().replace(/ /g, '-')}`}>
              {tagType}
            </Text>
          </Flex>
        </S.Box>
        {selectedTag ? <S.Divider borderColor={dividerColor} mt={2} width="120%" /> : null}
      </FlexItem>
    )
  }

  const HideButton = () => (
    <Flex justifyContent="flex-end">
      <Box
        cursor="pointer"
        onClick={() => {
          setSelectedTag('')
        }}
        data-qa="hide-tag-button">
        <Text color="primary" fontSize="12px" lineHeight="16px">
          Hide details
        </Text>
      </Box>
    </Flex>
  )

  return (
    <Box mt={2}>
      {selectedTag ? <HideButton /> : null}
      <HorizontalScroll>
        <Flex mt={2} alignItems="center">
          <Icon mr={2} name="tag" color="primary" />
          {taggedChildren && taggedChildren.length > 0 && (
            <TagButtons amount={taggedChildren?.length} tagType="Children" />
          )}
          {learningOutcomes && learningOutcomes.length > 0 && (
            <TagButtons amount={learningOutcomes?.length} tagType="Learning Outcomes" />
          )}
          {links && links.length > 0 && <TagButtons amount={links?.length} tagType="Links" />}
          {(futurePlans || planningDate) && <TagButtons tagType="Future Plans" />}
          {isPlayground && (!isPostPreview || reflections?.length) ? (
            <TagButtons tagType="Reflections" />
          ) : null}
          {assistLogFeatureFlag && isPlayground && !isPostPreview && aiAssistantLogs?.length ? (
            <TagButtons tagType="Assist Log" />
          ) : null}

          {selectedTag ? (
            <FlexItem flexGrow={1}>
              <S.Divider borderColor={colors.cosmicShade4} />
            </FlexItem>
          ) : null}
        </Flex>
      </HorizontalScroll>
      <ChildGrid tagType={selectedTag} taggedChildren={taggedChildren} />
      <LearningOutcomeGrid tagType={selectedTag} learningOutcomes={learningOutcomes} />
      <LinksGrid tagType={selectedTag} links={links} />
      <FuturePlans tagType={selectedTag} futurePlans={futurePlans} planningDate={planningDate} />
      <Reflections
        tagType={selectedTag}
        reflections={reflections}
        postId={postId}
        isPostPreview={isPostPreview}
      />
      <AiAssistLog tagType={selectedTag} aiAssistantLogs={aiAssistantLogs} />
    </Box>
  )
}

Tags.displayName = 'Tags'

export default React.memo(Tags)
